import {
  Price,
  PriceCalculationResult,
  OldDebugFields,
  PriceCalculationResultBase,
  NewDebugFields,
  ValidPriceEstimation
} from '../../types/types'
import * as R from 'ramda'
import { DebugLine } from 'nemo-pricing'
import { OldDebugLine } from '../../types/tables'
import moment from 'moment'
import { GroupedPriceBreakdown } from '../common/price-breakdown-types'
import { Validation } from 'io-ts'

export function round(num: number, decimals: number = 2) {
  const multiplier = Math.pow(10, decimals)
  return Math.round(num * multiplier) / multiplier
}

export function floor(num: number, decimals: number = 2) {
  const multiplier = Math.pow(10, decimals)
  return Math.floor(num * multiplier) / multiplier
}

export function getValidDiscount(price: number | null, discountPrice: number | null): number {
  return typeof price === 'number' && typeof discountPrice === 'number' && price > 0
    ? round(100 * (1 - discountPrice / price))
    : 0
}

export const sumPrices = (prices: ReadonlyArray<number>): number => round(R.sum(prices))

const discountedPrice = (price: Price) => (price.discountedPrice != null ? price.discountedPrice : 0)

const listPrice = (price: Price) => (price.equipmentPrice != null ? price.equipmentPrice : 0)

export const sumCorePrice = (prices: ReadonlyArray<Price>): number => sumPrices(prices.map(discountedPrice))

export const sumCoreVasDiscountedPrice = (prices: ReadonlyArray<Price>): number =>
  sumPrices(prices.map(price => price.discountedPrice || 0))

export const sumTotalPrice = (prices: ReadonlyArray<Price>): number => sumCorePrice(prices)

export const sumCoreListPrice = (prices: ReadonlyArray<Price>): number => sumPrices(prices.map(listPrice))

export const sumDiscountedPrice = (prices: ReadonlyArray<Price>): number => sumPrices(prices.map(discountedPrice))

export const sumTotalDiscountedPrice = (prices: ReadonlyArray<Price>): number => sumDiscountedPrice(prices)

export const sumListPrice = (prices: ReadonlyArray<Price>): number => sumCoreListPrice(prices)

export function isNewDebugFields(p: PriceCalculationResult): p is PriceCalculationResultBase & NewDebugFields {
  return (
    (p as NewDebugFields).debugCore != null &&
    (p as NewDebugFields).debugValueAddedServices != null &&
    (p as NewDebugFields).debugExtraServices != null
  )
}

function isOldDebugFields(p: PriceCalculationResult): p is PriceCalculationResultBase & OldDebugFields {
  return (p as OldDebugFields).debug != null
}

export function flattenDebugData(debugLines: ReadonlyArray<DebugLine>): DebugLine[] {
  return R.chain(l => [...flattenDebugData(l.children), l], debugLines)
}

export function getDebugData(p: PriceCalculationResult): ReadonlyArray<OldDebugLine> | DebugLine[] {
  return isOldDebugFields(p) ? p.debug : flattenDebugData(p.debugCore)
}

export function getDebugDataForVas(p: PriceCalculationResult): ReadonlyArray<OldDebugLine> | DebugLine[] {
  return isOldDebugFields(p) ? [] : flattenDebugData(p.debugValueAddedServices)
}

function getPriceBasedOnMonths(price: number, months: number) {
  return round((price / 12) * months)
}

/**
 *
 * @param price The annual price
 * @param days This is the spare days in a month.
 * @param daysInMonth The number of calendar days in the month.
 *
 * Method calculates the price/day with the number of calendar days in the month.
 */
export function getPriceBasedOnDays(price: number, days: number, daysInMonth: number) {
  return round((price / 12 / daysInMonth) * days)
}

const getTotalDaysInMonth = (month: number, year: number): number =>
  month === 2 ? [28, 29][Number(moment([year]).isLeapYear())] : [4, 6, 9, 11].includes(month) ? 30 : 31

/**
 *
 * @param price Annual price
 * @param fromDate Start date of the contract
 * @param toDate End date of the contract which is included for the spare days calculation
 *
 * Method calculates the total price of the date range that is being considered.
 * The duration is calculated between start and end date.
 * Begin by calculating the remaining days in the beginning month.
 * Then we identify if these remaining days is equal to the total number of days possible in this month.
 * e.g. fromDate = 2020-02-01, then remainingDaysInFromMonth = 29 (feb of leap year), isCompleteFromMonth = true.
 * This means we need to consider this as complete month. Add it to the month factor.
 * Similarly we identify if the days in the toDate is equal to the total number of days possible in this month.
 * e.g. toDate = 2020-02-29 (feb of leap year), isCompleteToMonth = true.
 * Add this as well to the month factor.
 * Next we calculate the number of years between fromDate and toDate.
 * Evaluate # of months by multiplying years by 12
 * and add to it the month factor along with the remaining months between the fromDate and toDate.
 *
 * As per the client's expectation, monthly is calculated for ex. 12-08-2020 to 11-09-2020 as a month.
 * Irrespective of 31, 30 or 29 days in a month. But the spare days is calculated exactly to the rest of the days in the calendar
 */
export function getPriceBasedOnMonthsAndDays(price: number, fromDate?: string, toDate?: string) {
  // check for empty string (''), null or undefined (missing) values of fromDate and toDate
  // We show Year 1 total yearly price in case Acceptance date is less than Contract Start Date
  if (!fromDate || !toDate) {
    return 0
  }
  // extract individual date, month, year of the from and to dates
  const fromDateSplit = fromDate.split('-')
  const toDateSplit = toDate.split('-')
  const fromDateNum = parseInt(fromDateSplit[2], 10)
  const toDateNum = parseInt(toDateSplit[2], 10)
  const fromMonthNum = parseInt(fromDateSplit[1], 10)
  const toMonthNum = parseInt(toDateSplit[1], 10)
  const fromYearNum = parseInt(fromDateSplit[0], 10)
  const toYearNum = parseInt(toDateSplit[0], 10)

  // Start logic to evaluate difference of days between dates
  const totalDaysInFromMonth = getTotalDaysInMonth(fromMonthNum, fromYearNum)
  const totalDaysInToMonth = getTotalDaysInMonth(toMonthNum, toYearNum)
  // Start date covers full month
  const isCompleteFromMonth = fromDateNum === 1
  // Acceptance date covers full month
  const isCompleteToMonth = toDateNum === totalDaysInToMonth

  const remainingDaysInFromMonth = totalDaysInFromMonth - (fromDateNum - 1)
  const daysInFromMonth = Number(!isCompleteFromMonth) * remainingDaysInFromMonth
  const daysInToMonth = Number(!isCompleteToMonth) * toDateNum
  const thresholdMaxDaysForMonth =
    Number(!isCompleteFromMonth) * totalDaysInFromMonth || Number(!isCompleteToMonth) * totalDaysInToMonth
  const daysCount = daysInFromMonth + daysInToMonth

  // Is difference in days more than or equal to the total days in start month
  const isMoreThanThresholdDaysNum = Number(daysCount > 0) * Number(daysCount >= thresholdMaxDaysForMonth)

  // Configure the additional months needed to be added to the month difference
  const monthFactor = Number(isCompleteFromMonth) + Number(isCompleteToMonth) + isMoreThanThresholdDaysNum

  const fromMonthGreaterOrEqualNumber = Number(toMonthNum < fromMonthNum)
  const years = toYearNum - fromYearNum - fromMonthGreaterOrEqualNumber
  const months = years * 12 + (monthFactor + (toMonthNum + 12 * fromMonthGreaterOrEqualNumber - fromMonthNum) - 1)

  const daysToConvertToMonth = isMoreThanThresholdDaysNum * thresholdMaxDaysForMonth
  const remainingDaysInFromMonthAfterConversion = Math.max(daysInFromMonth - daysToConvertToMonth, 0)
  const remainingDaysToConvertInToMonth = Math.max(daysToConvertToMonth - daysInFromMonth, 0)
  const remainingDaysInToMonthAfterConversion = Math.max(daysInToMonth - remainingDaysToConvertInToMonth, 0)

  return (
    getPriceBasedOnMonths(price, months) +
    getPriceBasedOnDays(price, remainingDaysInFromMonthAfterConversion, totalDaysInFromMonth) +
    getPriceBasedOnDays(price, remainingDaysInToMonthAfterConversion, totalDaysInToMonth)
  )
}

export function getFuturePriceBasedOnMonths(price: number, months: number) {
  return months > 0 ? price / months : 0
}

export function stringifyGroupPrice(
  pbd: GroupedPriceBreakdown,
  showPriceEstimation: boolean,
  defaultValue: any
): string {
  const { priceEstimationPricesVatExcluded, priceEstimationPricesVatIncluded } = pbd
  const getDecodeVal = (a: Validation<ReadonlyArray<ValidPriceEstimation>>): number[] =>
    showPriceEstimation
      ? a.fold(
          _ => defaultValue,
          (r: ReadonlyArray<ValidPriceEstimation>) => r.map(({ price }) => round(price))
        )
      : defaultValue

  const modifiedGroupPrice = {
    ...pbd,
    priceEstimationPricesVatExcluded: getDecodeVal(priceEstimationPricesVatExcluded),
    priceEstimationPricesVatIncluded: getDecodeVal(priceEstimationPricesVatIncluded)
  }
  return JSON.stringify(modifiedGroupPrice)
    .replace(/,/g, ',\n')
    .replace(/{/g, '{\n')
    .replace(/\[/g, '[\n')
    .replace(/},/g, '},\n\n')
    .replace(/\]/g, '\n]')
    .replace(/\]}/g, ']\n}')
}
